import React, { useEffect, useState, useSyncExternalStore } from 'react';
import {
  Sparkles, Wand2, FileText, X, AlertTriangle, CheckCircle2, XCircle, ExternalLink, RotateCcw, Save, Upload, Loader2,
} from 'lucide-react';
import Modal from '../Modal';
import Button from '../Button';
import { aiApi, AI_API } from '../../utils/aiApi';

// "Úprava PDF pomocí AI": the user writes what to change, Claude returns an edit plan, the server applies it to a
// NEW PDF. The user checks the list of changes and the preview, then saves it as a new document next to the
// original (which is never overwritten) or tries again.
// source: { kind: 'machine_doc', machineId, docId, name } | { kind: 'customer_file', customerId, docId, fileId, name }
//       | { kind: 'upload', machineId?, customerId? }

const ACCENT = '#E53935';
const BORDER = '#E0E0E0';
const MAX_INSTRUCTION = 2000;
const MAX_MB = 20;
const MOBILE_QUERY = '(max-width: 640px)';
const EXAMPLES = [
  'Oprav datum předání na 15. 10. 2026',
  'Doplň do pole Odběratel údaje zákazníka',
  'Přidej razítko ZAPLACENO na první stranu',
  'Smaž poslední stranu',
];

const inputStyle = {
  width: '100%', padding: '10px 12px', border: `1px solid ${BORDER}`, borderRadius: '8px',
  fontSize: '14px', background: '#fff', boxSizing: 'border-box', fontFamily: 'inherit', color: '#333',
};
const labelStyle = { fontSize: '12px', fontWeight: 600, color: '#666', display: 'block', marginBottom: '6px' };
const chipStyle = {
  border: `1px solid ${BORDER}`, background: '#FAFAFA', color: '#444', borderRadius: '16px', padding: '6px 12px',
  fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left', minHeight: '32px',
};

function subscribeMobile(callback) {
  const mq = window.matchMedia(MOBILE_QUERY);
  mq.addEventListener('change', callback);
  return () => mq.removeEventListener('change', callback);
}
const useIsMobile = () => useSyncExternalStore(subscribeMobile, () => window.matchMedia(MOBILE_QUERY).matches, () => false);

const stripPdf = (name) => String(name || '').replace(/\.pdf$/i, '').trim();
const hasErpLink = (source) => Boolean(source?.machineId || source?.customerId);

function planForm(source, { instruction, includeErp, file }) {
  const form = new FormData();
  form.append('instruction', instruction.trim());
  form.append('sourceKind', source.kind);
  form.append('includeErpData', includeErp ? '1' : '0');
  ['machineId', 'customerId', 'docId', 'fileId'].forEach((key) => {
    if (source[key] !== undefined && source[key] !== null && source[key] !== '') form.append(key, String(source[key]));
  });
  if (source.kind === 'upload' && file) form.append('file', file);
  return form;
}

function Notice({ tone = 'error', children }) {
  const colors = tone === 'error' ? { bg: '#FFEBEE', fg: '#C62828' } : { bg: '#FFF8E1', fg: '#8D6E00' };
  return (
    <div role={tone === 'error' ? 'alert' : undefined} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', background: colors.bg, color: colors.fg, borderRadius: '8px', padding: '10px 12px', fontSize: '14px' }}>
      <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: '1px' }} /> <div>{children}</div>
    </div>
  );
}

function Header({ source, onClose, busy }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' }}>
      <div style={{ minWidth: 0 }}>
        <h2 id="ai-pdf-editor-title" style={{ margin: 0, fontSize: '18px', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Wand2 size={20} color={ACCENT} /> Úprava PDF pomocí AI
        </h2>
        {source.name && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px', fontSize: '13px', color: '#666', minWidth: 0 }}>
            <FileText size={14} style={{ flexShrink: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{source.name}</span>
          </div>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 600, color: ACCENT, background: '#FFEBEE', borderRadius: '10px', padding: '2px 8px' }}>
            <Sparkles size={12} /> Zpracuje Claude
          </span>
          <span style={{ fontSize: '12px', color: '#888' }}>Originál zůstane beze změny.</span>
        </div>
      </div>
      <button type="button" onClick={onClose} disabled={busy} aria-label="Zavřít" style={{ background: 'none', border: 'none', cursor: busy ? 'not-allowed' : 'pointer', padding: '6px', color: '#666', borderRadius: '8px' }}>
        <X size={20} />
      </button>
    </div>
  );
}

function InstructionStep({ source, instruction, setInstruction, includeErp, setIncludeErp, file, setFile, loading, onSubmit }) {
  const erpAvailable = hasErpLink(source);
  const tooLong = instruction.length > MAX_INSTRUCTION;
  const missingFile = source.kind === 'upload' && !file;
  const canSubmit = instruction.trim() && !tooLong && !missingFile && !loading;

  const pickFile = (e) => {
    const picked = e.target.files?.[0] || null;
    e.target.value = '';
    if (picked && picked.size > MAX_MB * 1024 * 1024) { setFile({ error: `Soubor je větší než ${MAX_MB} MB.` }); return; }
    if (picked && picked.type !== 'application/pdf' && !/\.pdf$/i.test(picked.name)) { setFile({ error: 'Upravit lze jen PDF.' }); return; }
    setFile(picked);
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); if (canSubmit) onSubmit(); }} style={{ display: 'grid', gap: '14px' }}>
      {source.kind === 'upload' && (
        <div>
          <span style={labelStyle}>PDF k úpravě</span>
          <label style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '10px', border: `1px dashed ${file?.name ? ACCENT : '#BDBDBD'}`, borderRadius: '12px', padding: '14px', cursor: loading ? 'not-allowed' : 'pointer', background: '#FAFAFA' }}>
            <Upload size={18} color={file?.name ? ACCENT : '#666'} style={{ flexShrink: 0 }} />
            <span style={{ fontSize: '14px', color: '#444', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {file?.name || 'Vyberte PDF soubor (max. 20 MB)'}
            </span>
            <input type="file" accept="application/pdf,.pdf" onChange={pickFile} disabled={loading} style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }} />
          </label>
          {file?.error && <div role="alert" style={{ color: '#C62828', fontSize: '13px', marginTop: '6px' }}>{file.error}</div>}
        </div>
      )}

      <div>
        <label htmlFor="ai-pdf-instruction" style={labelStyle}>Co se má v dokumentu upravit?</label>
        <textarea
          id="ai-pdf-instruction" value={instruction} onChange={(e) => setInstruction(e.target.value)} disabled={loading} rows={4}
          placeholder="Např. „oprav datum předání na 15. 10. 2026 a doplň do pole Odběratel firmu Autoškola Novák“"
          style={{ ...inputStyle, resize: 'vertical', minHeight: '96px', lineHeight: 1.45 }}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && canSubmit) { e.preventDefault(); onSubmit(); } }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', fontSize: '12px', color: tooLong ? '#C62828' : '#999', marginTop: '4px' }}>
          {instruction.length} / {MAX_INSTRUCTION}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '4px' }}>
          {EXAMPLES.map((ex) => (
            <button key={ex} type="button" disabled={loading} style={chipStyle} onClick={() => setInstruction(ex)}>{ex}</button>
          ))}
        </div>
      </div>

      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '14px', color: erpAvailable ? '#333' : '#999', cursor: erpAvailable ? 'pointer' : 'default' }}>
        <input type="checkbox" checked={includeErp && erpAvailable} disabled={!erpAvailable || loading} onChange={(e) => setIncludeErp(e.target.checked)} style={{ width: 18, height: 18, marginTop: '1px', accentColor: ACCENT, flexShrink: 0 }} />
        <span>
          Použít údaje zákazníka / trenažéru z ERP
          <span style={{ display: 'block', fontSize: '12px', color: '#888', marginTop: '2px' }}>
            {erpAvailable ? 'Název, IČO, DIČ, adresa, kontakt, model a výrobní číslo, data smlouvy a předání.' : 'Dokument není přiřazený k zákazníkovi ani trenažéru.'}
          </span>
        </span>
      </label>

      <div aria-live="polite" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end', gap: '12px' }}>
        {loading && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#666', marginRight: 'auto' }}>
            <Loader2 size={16} className="animate-spin" color={ACCENT} /> Claude připravuje úpravy… (obvykle do minuty)
          </span>
        )}
        <Button type="submit" icon={Sparkles} loading={loading} disabled={!canSubmit}>Navrhnout úpravy</Button>
      </div>
    </form>
  );
}

function OperationList({ operations }) {
  if (!operations.length) return <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>Claude nenavrhl žádnou úpravu.</p>;
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '6px' }}>
      {operations.map((op, i) => {
        const ok = op.status === 'ok';
        return (
          <li key={i} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', padding: '8px 10px', border: `1px solid ${ok ? BORDER : '#FFCDD2'}`, borderRadius: '8px', background: ok ? '#fff' : '#FFF5F5' }}>
            {ok ? <CheckCircle2 size={18} color="#2E7D32" style={{ flexShrink: 0, marginTop: '1px' }} aria-label="Provedeno" /> : <XCircle size={18} color="#C62828" style={{ flexShrink: 0, marginTop: '1px' }} aria-label="Neprovedeno" />}
            <div style={{ minWidth: 0, fontSize: '14px', color: '#333', overflowWrap: 'anywhere' }}>
              {op.description}
              {!ok && op.error && <div style={{ fontSize: '13px', color: '#C62828', marginTop: '2px' }}>{op.error}</div>}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function Preview({ token, isMobile }) {
  const url = `${AI_API}/pdf/${token}/file`;
  const link = (
    <a href={url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: ACCENT, fontSize: '14px', fontWeight: 600, textDecoration: 'none' }}>
      <ExternalLink size={16} /> Otevřít náhled
    </a>
  );
  if (isMobile) {
    return <div style={{ border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '14px', display: 'flex', alignItems: 'center', gap: '10px' }}><FileText size={20} color="#666" />{link}</div>;
  }
  return (
    <div>
      <iframe title="Náhled upraveného PDF" src={url} style={{ width: '100%', height: '60vh', minHeight: '360px', border: `1px solid ${BORDER}`, borderRadius: '12px', background: '#F5F5F5' }} />
      <div style={{ marginTop: '6px' }}>{link}</div>
    </div>
  );
}

function ReviewStep({ result, title, setTitle, saving, onSave, onRetry, isMobile, needsTarget }) {
  const okCount = result.operations.filter((o) => o.status === 'ok').length;
  const pagesChanged = result.pageCount !== result.originalPageCount;
  return (
    <div style={{ display: 'grid', gap: '16px' }}>
      {result.summary && (
        <div style={{ background: '#FAFAFA', border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '12px 14px', fontSize: '14px', color: '#333', lineHeight: 1.5 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600, color: ACCENT, marginBottom: '4px' }}><Sparkles size={12} /> Návrh úprav</div>
          {result.summary}
        </div>
      )}

      <div>
        <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#333', margin: '0 0 8px' }}>
          Úpravy ({okCount} z {result.operations.length} provedeno{pagesChanged ? `, stran: ${result.originalPageCount} → ${result.pageCount}` : ''})
        </h3>
        <OperationList operations={result.operations} />
      </div>

      {result.warnings.length > 0 && (
        <Notice tone="warning">
          <ul style={{ margin: 0, paddingLeft: '18px' }}>{result.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </Notice>
      )}

      {result.changed ? <Preview token={result.token} isMobile={isMobile} /> : <Notice>Žádnou úpravu se nepodařilo provést. Zkuste pokyn upřesnit.</Notice>}

      {result.changed && (
        <div>
          <label htmlFor="ai-pdf-title" style={labelStyle}>Název nového dokumentu</label>
          <input id="ai-pdf-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={250} disabled={saving} style={inputStyle} />
          {needsTarget && <div style={{ fontSize: '13px', color: '#C62828', marginTop: '6px' }}>Dokument nelze uložit: není vybraný trenažér ani zákazník.</div>}
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'flex-end' }}>
        <Button variant="secondary" icon={RotateCcw} onClick={onRetry} disabled={saving} fullWidth={isMobile}>Zkusit jinak</Button>
        {result.changed && (
          <Button icon={Save} onClick={onSave} loading={saving} disabled={!title.trim() || needsTarget} fullWidth={isMobile}>Uložit jako nový dokument</Button>
        )}
      </div>
    </div>
  );
}

function EditorPanel({ source, onClose, onSaved }) {
  const isMobile = useIsMobile();
  const [phase, setPhase] = useState('edit'); // edit | loading | review | saving | saved
  const [instruction, setInstruction] = useState('');
  const [includeErp, setIncludeErp] = useState(false);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [title, setTitle] = useState('');
  const [error, setError] = useState('');
  const busy = phase === 'loading' || phase === 'saving';

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const submit = async () => {
    setError('');
    setPhase('loading');
    try {
      const data = await aiApi('/pdf/plan', { method: 'POST', form: planForm(source, { instruction, includeErp, file: file?.name ? file : null }) });
      setResult(data);
      setTitle(stripPdf(data.fileName));
      setPhase('review');
    } catch (err) {
      setError(err.message || 'Návrh úprav se nepodařilo připravit.');
      setPhase('edit');
    }
  };

  const save = async () => {
    setError('');
    setPhase('saving');
    try {
      const saved = await aiApi(`/pdf/${result.token}/save`, { method: 'POST', body: { title: title.trim(), machineId: source.machineId, customerId: source.customerId } });
      setPhase('saved');
      onSaved?.(saved);
    } catch (err) {
      setError(err.message || 'Dokument se nepodařilo uložit.');
      setPhase('review');
    }
  };

  const retry = () => { setError(''); setResult(null); setPhase('edit'); };

  return (
    <div
      role="dialog" aria-modal="true" aria-labelledby="ai-pdf-editor-title"
      style={{ background: '#fff', borderRadius: '12px', maxWidth: '960px', width: isMobile ? '100%' : 'calc(100% - 32px)', maxHeight: '92vh', overflow: 'auto', padding: isMobile ? '16px' : '24px', boxSizing: 'border-box' }}
    >
      <Header source={source} onClose={onClose} busy={busy} />
      {error && <div style={{ marginBottom: '14px' }}><Notice>{error}</Notice></div>}

      {(phase === 'edit' || phase === 'loading') && (
        <InstructionStep
          source={source} instruction={instruction} setInstruction={setInstruction} includeErp={includeErp} setIncludeErp={setIncludeErp}
          file={file} setFile={setFile} loading={phase === 'loading'} onSubmit={submit}
        />
      )}

      {(phase === 'review' || phase === 'saving') && result && (
        <ReviewStep
          result={result} title={title} setTitle={setTitle} saving={phase === 'saving'} onSave={save} onRetry={retry} isMobile={isMobile}
          needsTarget={source.kind === 'upload' && !hasErpLink(source)}
        />
      )}

      {phase === 'saved' && (
        <div role="status" style={{ display: 'grid', justifyItems: 'center', gap: '12px', padding: '24px 8px', textAlign: 'center' }}>
          <CheckCircle2 size={40} color="#2E7D32" />
          <div style={{ fontSize: '16px', fontWeight: 600, color: '#333' }}>Upravený dokument je uložený</div>
          <div style={{ fontSize: '14px', color: '#666' }}>„{title.trim()}“ najdete v seznamu dokumentů vedle originálu.</div>
          <Button variant="secondary" onClick={onClose}>Zavřít</Button>
        </div>
      )}
    </div>
  );
}

export default function AiPdfEditor({ open, onClose, source, onSaved }) {
  const key = source ? [source.kind, source.machineId, source.customerId, source.docId, source.fileId].join(':') : 'none';
  return (
    // no backdrop close: a stray click must not throw away a prepared edit
    <Modal show={open && Boolean(source)}>
      {open && source && <EditorPanel key={key} source={source} onClose={onClose} onSaved={onSaved} />}
    </Modal>
  );
}
